import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { 
  Shield, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  ShieldCheck,
  Lock
} from "lucide-react";
import { router } from "@inertiajs/react";

type Role = {
  id: number;
  name: string;
  description: string;
  is_system_role: boolean;
  permissions: Permission[];
};

type Permission = {
  id: number;
  name: string;
  resource: string;
  action: string;
  description: string;
};

type RoleManagementProps = {
  roles: Role[];
  permissions: Record<string, Permission[]>;
};

export default function RoleManagement({ roles, permissions }: RoleManagementProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permission_ids: [] as number[]
  });

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateRole = () => {
    router.post('/dashboard/settings/create_role', {
      role: formData
    }, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
        resetForm();
      }
    });
  };

  const handleUpdateRole = () => {
    if (!selectedRole) return;
    
    router.patch(`/dashboard/settings/${selectedRole.id}/update_role`, {
      role: formData
    }, {
      onSuccess: () => {
        setIsEditDialogOpen(false);
        resetForm();
        setSelectedRole(null);
      }
    });
  };

  const handleDeleteRole = (role: Role) => {
    if (confirm(`Tem certeza que deseja excluir o role ${role.name}?`)) {
      router.delete(`/dashboard/settings/${role.id}/destroy_role`);
    }
  };

  const openEditDialog = (role: Role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description,
      permission_ids: role.permissions.map(p => p.id)
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      permission_ids: []
    });
  };

  const handlePermissionChange = (permissionId: number, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        permission_ids: [...prev.permission_ids, permissionId]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        permission_ids: prev.permission_ids.filter(id => id !== permissionId)
      }));
    }
  };

  const getResourcePermissions = (resource: string) => {
    return permissions[resource] || [];
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Gerenciamento de Roles
            </CardTitle>
            <CardDescription>
              Gerencie roles e suas permissões no sistema
            </CardDescription>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Novo Role
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Criar Novo Role</DialogTitle>
                <DialogDescription>
                  Crie um novo role e defina suas permissões
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome do Role</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ex: Moderador"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Descrição</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Descreva as responsabilidades deste role"
                    />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Label>Permissões</Label>
                  <div className="grid gap-4">
                    {Object.keys(permissions).map(resource => (
                      <Card key={resource}>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm capitalize">{resource}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-3 gap-2">
                            {getResourcePermissions(resource).map(permission => (
                              <div key={permission.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`perm-${permission.id}`}
                                  checked={formData.permission_ids.includes(permission.id)}
                                  onCheckedChange={(checked) => handlePermissionChange(permission.id, checked as boolean)}
                                />
                                <Label htmlFor={`perm-${permission.id}`} className="text-sm">
                                  {permission.action}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateRole}>
                  Criar Role
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Search */}
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>

          {/* Roles Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Permissões</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        {role.name}
                      </div>
                    </TableCell>
                    <TableCell>{role.description}</TableCell>
                    <TableCell>
                      {role.is_system_role ? (
                        <Badge variant="default" className="flex items-center gap-1">
                          <Lock className="h-3 w-3" />
                          Sistema
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Customizado</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {role.permissions.slice(0, 3).map(permission => (
                          <Badge key={permission.id} variant="outline" className="text-xs">
                            {permission.action}_{permission.resource}
                          </Badge>
                        ))}
                        {role.permissions.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{role.permissions.length - 3} mais
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(role)}
                          disabled={role.is_system_role}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRole(role)}
                          disabled={role.is_system_role}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Edit Role Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Editar Role</DialogTitle>
              <DialogDescription>
                Atualize as informações e permissões do role
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Nome do Role</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Descrição</Label>
                  <Textarea
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <Label>Permissões</Label>
                <div className="grid gap-4">
                  {Object.keys(permissions).map(resource => (
                    <Card key={resource}>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm capitalize">{resource}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-2">
                          {getResourcePermissions(resource).map(permission => (
                            <div key={permission.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`edit-perm-${permission.id}`}
                                checked={formData.permission_ids.includes(permission.id)}
                                onCheckedChange={(checked) => handlePermissionChange(permission.id, checked as boolean)}
                              />
                              <Label htmlFor={`edit-perm-${permission.id}`} className="text-sm">
                                {permission.action}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUpdateRole}>
                Salvar Alterações
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}